import { useEffect, useRef, useState } from "react";

import { displayLocale, formatISODate, localizedDatePlaceholder, parseDisplayDate } from "./localization";

export function LocalizedDateInput({ id, label, value, min, max, required, onChange }: {
  id: string;
  label: string;
  value: string;
  min?: string;
  max?: string;
  required?: boolean;
  onChange: (value: string) => void;
}) {
  const nativeInput = useRef<HTMLInputElement>(null);
  const textInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(() => formatISODate(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (document.activeElement !== textInput.current) setDraft(formatISODate(value));
  }, [value]);

  function commit() {
    const parsed = parseDisplayDate(draft);
    const accepted = Boolean(parsed && (!min || parsed >= min) && (!max || parsed <= max));
    setInvalid(!accepted);
    if (accepted) {
      setDraft(formatISODate(parsed));
      onChange(parsed);
    } else {
      onChange("");
    }
  }

  function openPicker() {
    const input = nativeInput.current;
    if (!input) return;
    if (typeof input.showPicker === "function") input.showPicker();
    else input.click();
  }

  return <div className="localized-date-input">
    <input
      ref={textInput}
      id={id}
      aria-label={label}
      aria-invalid={invalid || undefined}
      inputMode="numeric"
      placeholder={localizedDatePlaceholder()}
      required={required}
      value={draft}
      onChange={(event) => {
        const nextDraft = event.target.value;
        const parsed = parseDisplayDate(nextDraft);
        setDraft(nextDraft);
        setInvalid(false);
        if (parsed && (!min || parsed >= min) && (!max || parsed <= max)) onChange(parsed);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        commit();
        event.currentTarget.blur();
      }}
    />
    <button type="button" aria-label="Kalender öffnen" title={`${label}: Kalender öffnen`} onClick={openPicker}><span aria-hidden="true">▦</span></button>
    <input
      ref={nativeInput}
      className="localized-date-native"
      aria-hidden="true"
      tabIndex={-1}
      lang={displayLocale}
      type="date"
      min={min}
      max={max}
      value={value}
      onChange={(event) => {
        setInvalid(false);
        setDraft(formatISODate(event.target.value));
        onChange(event.target.value);
      }}
    />
  </div>;
}
